import {
  createContext,
  useContext,
  type Dispatch,
  type FC,
  type MutableRefObject,
  type SetStateAction,
  useRef,
  useState,
  type ReactNode,
} from "react";

import type {
  CommandBarPageContext,
  CommandScope,
} from "src/components/commandbar/types";

export type CommandBarContextType = {
  isFetching: boolean;
  setIsFetching: (isFetching: boolean) => void;
};

export const CommandBarContext = createContext<CommandBarContextType>({
  isFetching: false,
  setIsFetching: () => {},
});
CommandBarContext.displayName = "CommandBarContext";

// Marks the palette dialog, so focus checks can tell it from a page modal.
export const COMMAND_PALETTE_ATTRIBUTE = "data-command-palette";

// Separate value and setter contexts keep pages that set it, and the app-level action hooks, from re-rendering when it changes.
const PageContextValue = createContext<CommandBarPageContext | null>(null);
const PageContextSetter = createContext<
  Dispatch<SetStateAction<CommandBarPageContext | null>>
>(() => {});

// The scope the next open starts in, set by a control just before it opens the palette (see takeInitialScope).
const InitialScope = createContext<MutableRefObject<CommandScope | null>>({
  current: null,
});

export const useCommandBar = () => useContext(CommandBarContext);

export const useCommandBarInitialScope = () => useContext(InitialScope);

export const useCommandBarPageContext = () => useContext(PageContextValue);

export const useSetCommandBarPageContext = () => useContext(PageContextSetter);

interface Props {
  children: ReactNode;
}

export const CommandBarProvider: FC<Props> = ({ children }) => {
  const [isFetching, setIsFetching] = useState(false);
  const [pageContext, setPageContext] = useState<CommandBarPageContext | null>(
    null,
  );
  const initialScope = useRef<CommandScope | null>(null);
  return (
    <InitialScope.Provider value={initialScope}>
      <PageContextSetter.Provider value={setPageContext}>
        <PageContextValue.Provider value={pageContext}>
          <CommandBarContext.Provider value={{ isFetching, setIsFetching }}>
            {children}
          </CommandBarContext.Provider>
        </PageContextValue.Provider>
      </PageContextSetter.Provider>
    </InitialScope.Provider>
  );
};
